import { PEPModal } from '@dealstack/frontend/shared/components/PEPModal/PEPModal';
import { Circle } from '@dealstack/frontend/shared/components/common/Circle';
import { GreyBox } from '@dealstack/frontend/shared/components/common/GreyBox';
import {
	INVESTOR_TEST_ELEMENT_ID,
	UIElementIdentifier,
} from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';
import { CheckboxGroupV2 } from '@dealstack/frontend/shared/components/forms/CheckboxGroup/CheckboxGroup.v2';
import { FormCheckbox } from '@dealstack/frontend/shared/components/forms/FormCheckbox/FormCheckbox';
import { TextArea } from '@dealstack/frontend/shared/components/forms/TextArea/TextArea';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import {
	TransInvestor,
	useTranslationInvestor,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { Checkbox as MantineCheckbox, Flex, Text as MantineText, Title } from '@mantine/core';
import { FormProvider } from 'react-hook-form';
import styled from 'styled-components';
import { KycWorkflowSettingResponseDto } from '@dealstack/shared/lib/generated';

import {
	DECLARATIONS_FORM,
	FUNDS_CHECKBOXES,
	SOURCE_OF_FUNDS_TYPE,
	useInvestmentManageDeclarationsController,
} from './useInvestmentManageDeclarationsController';

interface Props {
	isPIVInvestment: boolean;
	setIsContinueDisabled: (isContinueDisabled: boolean) => void;
	setIsLoading: (isLoading: boolean) => void;
	kycWorkflowId: string;
	kycSetting: KycWorkflowSettingResponseDto | undefined;
}

export const InvestmentManageDeclarations = ({
	setIsContinueDisabled,
	setIsLoading,
	isPIVInvestment,
	kycWorkflowId,
	kycSetting,
}: Props) => {
	const tInvestor = useTranslationInvestor();

	const {
		declarationsForm,
		isPEPModalVisible,
		setPEPModalVisible,
		isDeclarationsRejected,
		rejectionComment,
		isBeneficialOwnershipRequired,
		isSourceOfFundsRequired,
		isPepRequired,
	} = useInvestmentManageDeclarationsController({
		setIsContinueDisabled,
		setIsLoading,
		kycWorkflowId,
		kycSetting,
	});

	const isPep = declarationsForm.watch(DECLARATIONS_FORM.IS_POLITICALLY_EXPOSED_PERSON);
	const showSourceOfFundsTextarea = !!declarationsForm
		.watch(DECLARATIONS_FORM.SOURCE_OF_FUNDS)
		?.includes(SOURCE_OF_FUNDS_TYPE.OTHER);

	// Only render (and number) the declaration sections that are required for this workflow.
	let sectionCounter = 0;
	const boSectionNumber = isBeneficialOwnershipRequired ? ++sectionCounter : null;
	const sofSectionNumber = isSourceOfFundsRequired ? ++sectionCounter : null;
	const pepSectionNumber = isPepRequired ? ++sectionCounter : null;

	return (
		<div style={{ maxWidth: '40rem', whiteSpace: 'pre-line' }}>
			<FormProvider {...declarationsForm}>
				<MantineText>{tInvestor('investment.join.declaration.descr')}</MantineText>

				<DeclarationsWrapper>
					{isDeclarationsRejected && (
						<MantineText c="red.6">
							{tInvestor('investment.join.kyc.declarations.rejected')}
							{!!rejectionComment &&
								tInvestor('investment.join.kyc.declarations.rejected_comment', {
									rejectionComment,
								})}
						</MantineText>
					)}

					{isBeneficialOwnershipRequired && (
						<SingleDeclarationWrapper>
							<Flex align="center" gap="0.5rem">
								<Circle variant="filled">
									<MantineText c="gray.0" fz="lg" fw={600}>
										{boSectionNumber}
									</MantineText>
								</Circle>
								<Title order={5}>
									{tInvestor('investment.join.declaration.beneficial_ownership')}
								</Title>
							</Flex>

							<GreyBox>
								<UIElementIdentifier
									identifier={
										INVESTOR_TEST_ELEMENT_ID.INVESTOR_DECLARATIONS_BENEFICIAL_OWNERSHIP_CHECKBOX
									}
								>
									<FormCheckbox
										name={DECLARATIONS_FORM.BENEFICIAL_OWNERSHIP}
										label={tInvestor(
											`investment.join.kyc.declarations.beneficial.${
												isPIVInvestment ? 'label_piv' : 'label_individual'
											}`
										)}
									/>
								</UIElementIdentifier>
							</GreyBox>
						</SingleDeclarationWrapper>
					)}

					{isSourceOfFundsRequired && (
						<SingleDeclarationWrapper>
							<Flex align="center" gap="0.5rem">
								<Circle variant="filled">
									<MantineText c="gray.0" fz="lg" fw={600}>
										{sofSectionNumber}
									</MantineText>
								</Circle>
								<Title order={5}>
									{tInvestor('investment.join.declaration.source_funds')}
								</Title>
							</Flex>

							<GreyBox>
								<Title order={5} sx={{ marginBottom: '0.5rem' }}>
									{tInvestor('investment.join.declaration.source_funds_descr')}
								</Title>
								<UIElementIdentifier
									identifier={
										INVESTOR_TEST_ELEMENT_ID.INVESTOR_DECLARATIONS_SOURCE_OF_FUNDS_CHECKBOX
									}
								>
									<CheckboxGroupV2 name={DECLARATIONS_FORM.SOURCE_OF_FUNDS}>
										<Flex direction="column" gap="0.5rem">
											{FUNDS_CHECKBOXES.map((checkbox) => (
												<UIElementIdentifier
													key={checkbox.name}
													identifier={
														checkbox.testId as INVESTOR_TEST_ELEMENT_ID
													}
												>
													<MantineCheckbox
														value={checkbox.name}
														label={tInvestor(checkbox.label)}
													/>
												</UIElementIdentifier>
											))}
										</Flex>
									</CheckboxGroupV2>
								</UIElementIdentifier>

								{showSourceOfFundsTextarea && (
									<UIElementIdentifier
										identifier={
											INVESTOR_TEST_ELEMENT_ID.INVESTOR_DECLARATIONS_SOURCE_OF_FUNDS_OTHER_DETAILS
										}
									>
										<TextArea
											label={tInvestor(
												'investment.join.kyc.declarations.pep.details_label'
											)}
											name={DECLARATIONS_FORM.SOURCE_OF_FUNDS_OTHER_DETAILS}
											rows={2}
											fullWidth
										/>
									</UIElementIdentifier>
								)}
							</GreyBox>
						</SingleDeclarationWrapper>
					)}

					{isPepRequired && (
						<SingleDeclarationWrapper>
							<Flex align="center" gap="0.5rem">
								<Circle variant="filled">
									<MantineText c="gray.0" fz="lg" fw={600}>
										{pepSectionNumber}
									</MantineText>
								</Circle>
								<Title order={5}>
									{tInvestor('investment.join.declaration.pep')}
								</Title>
							</Flex>
							<MantineText>
								<TransInvestor
									components={{
										open: (
											<Text
												i18NextInline
												colorLink
												isLink
												bold
												variant="caption"
												onClick={() => setPEPModalVisible(true)}
											/>
										),
									}}
									i18nKey="investment.join.kyc.declarations.pep.subtitle"
								/>
							</MantineText>

							<GreyBox>
								<Title order={5} sx={{ marginBottom: '0.5rem' }}>
									{tInvestor('investment.join.declaration.pep_question')}
								</Title>

								<UIElementIdentifier
									identifier={
										INVESTOR_TEST_ELEMENT_ID.INVESTOR_DECLARATIONS_PEP_PERSON_CHECKBOX
									}
								>
									<Flex direction="column" gap="0.5rem">
										<UIElementIdentifier
											identifier={INVESTOR_TEST_ELEMENT_ID.NOT_PEP_CHECKBOX}
										>
											<MantineCheckbox
												checked={isPep === false}
												onClick={() =>
													declarationsForm.setValue(
														DECLARATIONS_FORM.IS_POLITICALLY_EXPOSED_PERSON,
														isPep === false ? undefined : false,
														{ shouldValidate: true }
													)
												}
												label={tInvestor(
													'investment.join.kyc.declarations.pep.label_1'
												)}
												disabled={declarationsForm.watch(
													DECLARATIONS_FORM.IS_POLITICALLY_EXPOSED_PERSON
												)}
											/>
										</UIElementIdentifier>
										<UIElementIdentifier
											identifier={INVESTOR_TEST_ELEMENT_ID.IS_PEP_CHECKBOX}
										>
											<MantineCheckbox
												checked={isPep}
												onClick={() =>
													declarationsForm.setValue(
														DECLARATIONS_FORM.IS_POLITICALLY_EXPOSED_PERSON,
														isPep ? undefined : true,
														{ shouldValidate: true }
													)
												}
												label={tInvestor(
													'investment.join.kyc.declarations.pep.label_2'
												)}
												disabled={
													declarationsForm.watch(
														DECLARATIONS_FORM.IS_POLITICALLY_EXPOSED_PERSON
													) === false
												}
											/>
										</UIElementIdentifier>

										{!!declarationsForm.formState.errors[
											DECLARATIONS_FORM.IS_POLITICALLY_EXPOSED_PERSON
										]?.message && (
											<MantineText c="red.6">
												{
													declarationsForm.formState.errors[
														DECLARATIONS_FORM
															.IS_POLITICALLY_EXPOSED_PERSON
													]?.message
												}
											</MantineText>
										)}
									</Flex>
								</UIElementIdentifier>
								{isPep && (
									<UIElementIdentifier
										identifier={
											INVESTOR_TEST_ELEMENT_ID.INVESTOR_DECLARATIONS_PEP_PERSON_DETAILS
										}
									>
										<TextArea
											label={tInvestor(
												'investment.join.kyc.declarations.pep.details_label'
											)}
											name={DECLARATIONS_FORM.PEP_DETAILS}
											rows={4}
											fullWidth
										/>
									</UIElementIdentifier>
								)}
							</GreyBox>
						</SingleDeclarationWrapper>
					)}
				</DeclarationsWrapper>
			</FormProvider>
			{isPEPModalVisible ? <PEPModal onClose={() => setPEPModalVisible(false)} /> : null}
		</div>
	);
};
const DeclarationsWrapper = styled.div`
	margin-top: 2.5em;
	display: flex;
	flex-direction: column;
	gap: 2em;
`;

const SingleDeclarationWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 2em;

	${TextArea} {
		margin-top: 0.5rem;
	}
`;
